import React, { useState } from 'react';
import {
  CreditCard,
  Monitor,
  Laptop2,
  ScanLine,
  LayoutTemplate,
  Tv,
  MonitorSmartphone,
  Tag,
  MonitorPlay,
  Columns3,
  Projector,
  Code2,
  ArrowUpRight,
  Play,
  X,
} from 'lucide-react';
import { useSite } from '../context/SiteContext';

const iconMap = {
  CreditCard,
  Monitor,
  Laptop2,
  ScanLine,
  LayoutTemplate,
  Tv,
  MonitorSmartphone,
  Tag,
  MonitorPlay,
  Columns3,
  Projector,
  Code2,
};

const Solutions = () => {
  const { solutions = [] } = useSite();
  const [videoId, setVideoId] = useState(null);

  return (
    <section id="solutions" className="section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-brand-green-light">What we deliver</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-white leading-tight">
              Solutions engineered for modern business.
            </h2>
          </div>
          <p className="max-w-md text-slate-400">
            End-to-end hardware and software from world-class partners — designed, deployed
            and supported by our certified engineers.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((s) => {
            const Icon = iconMap[s.icon] || CreditCard;
            return (
              <article
                key={s.id}
                id={s.id}
                className="group relative rounded-2xl overflow-hidden border border-white/8 bg-white/[0.02] card-lift"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover btn-transition group-hover:scale-[1.06]"
                    style={{ transitionDuration: '600ms' }}
                    loading="lazy"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-x-0 top-0 h-1/2"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(6,8,13,0.6) 0%, rgba(6,8,13,0) 100%)',
                    }}
                  />
                  {s.videoId && (
                    <button
                      onClick={() => setVideoId(s.videoId)}
                      className="absolute inset-0 flex items-center justify-center"
                      aria-label={`Play ${s.title} video`}
                    >
                      <span className="h-14 w-14 rounded-full bg-brand-green text-black flex items-center justify-center shadow-lg shadow-brand-green/40 group-hover:scale-110 btn-transition">
                        <Play className="h-5 w-5 fill-current" />
                      </span>
                    </button>
                  )}
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-brand-green/10 border border-brand-green/30 flex items-center justify-center">
                      <Icon className="h-4 w-4 text-brand-green-light" />
                    </div>
                    <h3 className="text-white text-lg font-semibold leading-snug">{s.title}</h3>
                  </div>
                  <p className="mt-3 text-sm text-slate-400">{s.tagline}</p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {s.partners.slice(0, 3).map((p) => (
                      <span
                        key={p}
                        className="text-[11px] px-2 py-1 rounded-md bg-white/[0.04] border border-white/8 text-slate-300"
                      >
                        {p}
                      </span>
                    ))}
                    {s.partners.length > 3 && (
                      <span className="text-[11px] px-2 py-1 rounded-md text-brand-pink-light">
                        +{s.partners.length - 3} more
                      </span>
                    )}
                  </div>

                  <a
                    href={s.href || `#${s.id}`}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm text-brand-green-light hover:text-white btn-transition"
                  >
                    Learn more <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {videoId && (
        <div
          className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setVideoId(null)}
        >
          <div
            className="relative w-full max-w-5xl aspect-video rounded-xl overflow-hidden border border-white/10 bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setVideoId(null)}
              aria-label="Close"
              className="absolute -top-3 -right-3 z-10 h-9 w-9 rounded-full bg-white text-black flex items-center justify-center hover:bg-brand-green-light btn-transition"
            >
              <X className="h-4 w-4" />
            </button>
            <iframe
              title="Solution video"
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Solutions;
